import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { hudBasePath, hudCopy, hudModules, hudPageMeta, hudPathOrder, HudPath, hudPaths, toHudCanonicalUrl } from './content';
import { CommandNav, CookieConsentBanner, GridBackdrop, RouteActivationOverlay, SystemButton, TransitionLayer } from './primitives';
import {
  AiAutomationPage,
  CommunicationsPage,
  CookiePolicyPage,
  PrivacyPolicyPage,
  PromotionTermsPage,
  ServiceArchitecturePage,
  StartPanelPage,
  TermsOfServicePage,
  VerifiedOperationsPage,
  WorkflowDeliveryPage
} from './pages';

const normalizePath = (rawPath: string): string => {
  const stripped = rawPath.replace(/^#/, '').trim();
  if (!stripped || stripped === '/') {
    return hudPaths.home;
  }

  const withLeadingSlash = stripped.startsWith('/') ? stripped : `/${stripped}`;
  const compressed = withLeadingSlash.replace(/\/{2,}/g, '/');
  const withoutTrailingSlash = compressed.endsWith('/') ? compressed.slice(0, -1) : compressed;

  return withoutTrailingSlash || hudPaths.home;
};

const isKnownHudPath = (path: string): path is HudPath => {
  return hudPathOrder.some((knownPath) => knownPath === path);
};

const stripHudBasePath = (pathname: string): string => {
  if (pathname === hudBasePath || pathname === `${hudBasePath}/`) {
    return '/';
  }

  if (pathname.startsWith(`${hudBasePath}/`)) {
    return pathname.slice(hudBasePath.length);
  }

  return pathname;
};

const resolvePathFromLocation = (): HudPath => {
  const hashPath = normalizePath(window.location.hash);
  if (window.location.hash && isKnownHudPath(hashPath)) {
    return hashPath;
  }

  const pathnamePath = normalizePath(stripHudBasePath(window.location.pathname));
  return isKnownHudPath(pathnamePath) ? pathnamePath : hudPaths.home;
};

const BootSequenceOverlay = ({ onComplete }: { onComplete: () => void }) => {
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setVisible(false);
    }, reducedMotion ? 350 : 1350);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [reducedMotion]);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {visible ? (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.45 } }}
          className="pointer-events-none fixed inset-0 z-[70] bg-[radial-gradient(circle_at_50%_50%,rgba(5,15,14,0.96),rgba(2,6,9,0.98))]"
          aria-hidden="true"
        >
          <motion.div
            className="absolute inset-y-0 left-[-30%] w-[28%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.3),transparent)]"
            animate={reducedMotion ? undefined : { x: ['0%', '520%'], opacity: [0, 0.95, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          />
          <div className="absolute left-1/2 top-1/2 w-[min(88vw,38rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-brand-lime/35 bg-brand-panel/80 p-5">
            <p className="hud-micro text-brand-lime">DEVELOGIC // BOOT SEQUENCE</p>
            <motion.p
              className="mt-3 text-sm text-brand-text-2"
              animate={reducedMotion ? undefined : { opacity: [0.45, 1, 0.55] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              Initializing command surfaces, telemetry channels, and subsystem modules...
            </motion.p>
            <div className="mt-4 h-2 overflow-hidden rounded-full border border-brand-lime/30 bg-brand-panel-2">
              <motion.div
                className="h-full bg-[linear-gradient(90deg,rgba(139,255,62,0.82),rgba(88,247,182,0.92))]"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: reducedMotion ? 0.35 : 1.2, ease: [0.2, 1, 0.32, 1] }}
              />
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const HomeReentryOverlay = ({ visible }: { visible: boolean }) => {
  const reducedMotion = useReducedMotion();

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          key="home-reentry-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0.12 : 0.28, ease: [0.2, 1, 0.32, 1] }}
          className="pointer-events-none fixed inset-0 z-[69] bg-[radial-gradient(circle_at_50%_50%,rgba(6,18,18,0.92),rgba(2,6,9,0.96))]"
          aria-hidden="true"
        >
          <motion.div
            className="absolute inset-y-0 left-[-30%] w-[28%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.24),transparent)]"
            animate={reducedMotion ? undefined : { x: ['0%', '520%'], opacity: [0.05, 0.72, 0.08] }}
            transition={{ duration: 0.72, ease: 'linear' }}
          />
          <div className="absolute left-1/2 top-1/2 w-[min(84vw,32rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-brand-lime/30 bg-brand-panel/78 p-4">
            <p className="hud-micro text-brand-lime">COMMAND CENTER // RE-ENTRY</p>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full border border-brand-lime/25 bg-brand-panel-2">
              <motion.div
                className="h-full bg-[linear-gradient(90deg,rgba(139,255,62,0.8),rgba(88,247,182,0.9))]"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: reducedMotion ? 0.18 : 0.48, ease: [0.2, 1, 0.32, 1] }}
              />
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const FooterModule = () => {
  return (
    <footer className="relative z-20 border-t border-brand-lime/20 px-4 py-10 sm:px-6 lg:px-8">
      <div className="hud-shell grid gap-5 lg:grid-cols-[1.2fr_0.9fr_0.9fr]">
        <div>
          <p className="font-display text-lg text-brand-text">{hudCopy.footer.title}</p>
          <p className="mt-2 max-w-2xl text-sm text-brand-text-2">{hudCopy.footer.summary}</p>
        </div>

        <div>
          <p className="hud-micro text-brand-lime">Resources</p>
          <ul className="mt-3 grid gap-2 text-sm text-brand-text-2">
            {hudCopy.footer.resources.map((resource) => (
              <li key={resource.label}>
                <a
                  href={resource.href}
                  target={resource.href.startsWith('http') ? '_blank' : undefined}
                  rel={resource.href.startsWith('http') ? 'noreferrer noopener' : undefined}
                  className="hover:text-brand-text"
                >
                  {resource.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="hud-micro text-brand-lime">Social</p>
          <ul className="mt-3 grid gap-2 text-sm text-brand-text-2">
            {hudCopy.footer.social.map((item) => (
              <li key={item.label}>
                <a href={item.href} target="_blank" rel="noreferrer noopener" className="hover:text-brand-text">
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="hud-shell mt-7 border-t border-brand-lime/15 pt-4">
        <p className="text-xs text-brand-text-3">Copyright © {new Date().getFullYear()} DeveLogic Digital. All rights reserved.</p>
      </div>
    </footer>
  );
};

const renderPage = (path: HudPath, startupReady: boolean) => {
  switch (path) {
    case hudPaths.home:
      return <StartPanelPage activateStartup={startupReady} />;
    case hudPaths.serviceArchitecture:
      return <ServiceArchitecturePage />;
    case hudPaths.automationSystems:
      return <AiAutomationPage />;
    case hudPaths.workflowPipeline:
      return <WorkflowDeliveryPage />;
    case hudPaths.verifiedOperations:
      return <VerifiedOperationsPage />;
    case hudPaths.communicationsTerminal:
      return <CommunicationsPage />;
    case hudPaths.termsOfService:
      return <TermsOfServicePage />;
    case hudPaths.privacyPolicy:
      return <PrivacyPolicyPage />;
    case hudPaths.cookiePolicy:
      return <CookiePolicyPage />;
    case hudPaths.promotionTerms:
      return <PromotionTermsPage />;
    default:
      return <StartPanelPage />;
  }
};

export const HudLandingPage = () => {
  const reducedMotion = useReducedMotion();
  const [startupReady, setStartupReady] = useState(false);
  const [showHomeReentryLoader, setShowHomeReentryLoader] = useState(false);
  const [fullscreenEnabled, setFullscreenEnabled] = useState(false);
  const [fullscreenActive, setFullscreenActive] = useState(false);
  const [activePath, setActivePath] = useState<HudPath>(() => {
    if (typeof window === 'undefined') {
      return hudPaths.home;
    }
    return resolvePathFromLocation();
  });
  const previousPathRef = useRef<HudPath>(activePath);

  useEffect(() => {
    const syncPathFromLocation = () => {
      const nextPath = resolvePathFromLocation();
      setActivePath(nextPath);

      const canonicalUrl = toHudCanonicalUrl(nextPath);
      const currentUrl = `${window.location.pathname}${window.location.hash}`;
      if (currentUrl !== canonicalUrl) {
        window.history.replaceState(null, '', canonicalUrl);
      }
    };

    syncPathFromLocation();
    window.addEventListener('hashchange', syncPathFromLocation);
    window.addEventListener('popstate', syncPathFromLocation);

    return () => {
      window.removeEventListener('hashchange', syncPathFromLocation);
      window.removeEventListener('popstate', syncPathFromLocation);
    };
  }, []);

  useEffect(() => {
    const updateFullscreenState = () => {
      setFullscreenEnabled(Boolean(document.fullscreenEnabled));
      setFullscreenActive(Boolean(document.fullscreenElement));
    };

    updateFullscreenState();
    document.addEventListener('fullscreenchange', updateFullscreenState);

    return () => {
      document.removeEventListener('fullscreenchange', updateFullscreenState);
    };
  }, []);

  useEffect(() => {
    const cameFromNonHome = previousPathRef.current !== hudPaths.home;
    if (startupReady && activePath === hudPaths.home && cameFromNonHome) {
      setShowHomeReentryLoader(true);
      const timeout = window.setTimeout(() => {
        setShowHomeReentryLoader(false);
      }, reducedMotion ? 160 : 520);

      previousPathRef.current = activePath;
      return () => {
        window.clearTimeout(timeout);
      };
    }

    previousPathRef.current = activePath;
  }, [activePath, reducedMotion, startupReady]);

  const activePage = hudPageMeta[activePath];
  const homeActive = activePath === hudPaths.home;
  const fullscreenToggleLabel = fullscreenActive ? 'Exit Fullscreen' : 'Enter Fullscreen';

  const toggleFullscreen = async () => {
    if (!fullscreenEnabled) {
      return;
    }

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      await document.documentElement.requestFullscreen();
    } catch {
      // Ignore rejected fullscreen requests and keep current state.
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-clip bg-brand-bg text-brand-text">
      <BootSequenceOverlay onComplete={() => setStartupReady(true)} />
      <HomeReentryOverlay visible={showHomeReentryLoader} />
      <RouteActivationOverlay routeKey={activePath} />
      <GridBackdrop />
      <div className={`fixed right-3 z-[62] hidden lg:block ${homeActive ? 'top-3' : 'top-[4.9rem]'}`}>
        <SystemButton
          type="button"
          variant={fullscreenActive ? 'primary' : 'secondary'}
          className="px-3 py-2 text-[0.55rem]"
          disabled={!fullscreenEnabled}
          onClick={toggleFullscreen}
          aria-pressed={fullscreenActive}
          aria-label={fullscreenToggleLabel}
        >
          {fullscreenToggleLabel}
        </SystemButton>
      </div>

      {homeActive ? null : <CommandNav activePath={activePath} routes={hudModules} terminalPath={hudPaths.communicationsTerminal} />}

      <main id="main-content" className={`relative z-10 ${homeActive ? 'pb-8 pt-0 xl:pb-8' : 'pb-28 pt-20 xl:pb-14'}`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activePath}
            initial={{ opacity: 0, y: 24, filter: 'blur(3px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -16, filter: 'blur(2px)' }}
            transition={{ duration: 0.45, ease: [0.2, 1, 0.32, 1] }}
          >
            <TransitionLayer label={`${activePage.shortLabel} // SCREEN ACTIVATED`} mode={activePage.mode} />
            {renderPage(activePath, startupReady)}
          </motion.div>
        </AnimatePresence>
      </main>

      {homeActive ? null : <FooterModule />}
      <CookieConsentBanner />
    </div>
  );
};
